import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recordingAPI } from '../services/api/api';
import RecordRTC from 'recordrtc';
import { 
  Play, 
  Square, 
  Camera, 
  Mic, 
  CheckCircle, 
  AlertCircle,
  Heart,
  Timer,
  Upload,
  ArrowRight,
  ArrowLeft,
  FileText,
  SkipForward
} from 'lucide-react';
import toast from 'react-hot-toast';

const PatientRecording = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completionStatus, setCompletionStatus] = useState(null);
  
  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [step, setStep] = useState('instructions'); // 'instructions', 'questionnaire', 'recording', 'uploading', 'results'
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Navigation state
  const [finalVitals, setFinalVitals] = useState(null);
  
  // Navigation state
  const [navigating, setNavigating] = useState(false);
  const [navigationDirection, setNavigationDirection] = useState(null); // 'next' or 'previous'
  
  // Camera preview state
  const [cameraInitialized, setCameraInitialized] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const initializingRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();
    
    console.log('🔧 useEffect mounting, creating new AbortController');
    
    const loadSessionData = async () => {
      if (!mounted) {
        console.log('⚠️ Component not mounted, skipping API call');
        return;
      }
      
      try {
        console.log('🔄 Loading session data for token:', token);
        
        // Make API call without abort signal to test if that's causing the hang
        console.log('🔄 Making API call without AbortController (testing)');
        
        // Add a timeout to the request
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000)
        );
        
        const response = await Promise.race([
          recordingAPI.getRecordingSession(token),
          timeoutPromise
        ]);
        const data = response.data;
        
        if (!mounted) {
          console.log('⚠️ Component unmounted during API call, ignoring response');
          return;
        }
        
        console.log('✅ Session data loaded successfully:', data);
        setSessionData(data);
        setQuestions(data.questions || []);
        setCompletionStatus({ 
          total_questions: data.total_questions || 0,
          completed_questions: 0,
          is_complete: false,
          completion_percentage: 0
        });
        console.log('✅ State updated successfully');
      } catch (error) {
        // If the request was aborted, don't treat it as an error
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          console.log('🔄 Request cancelled due to component unmount - this is normal');
          return;
        }
        
        if (!mounted) {
          console.log('⚠️ Component unmounted during error handling, ignoring error');
          return;
        }
        
        console.error('❌ Load session error:', error);
        console.error('❌ Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
          name: error.name
        });
        toast.error('Recording session not found or expired');
        setTimeout(() => {
          if (mounted) {
            console.log('🔄 Navigating to home page due to error');
            navigate('/');
          }
        }, 3000);
      } finally {
        // ALWAYS set loading to false, regardless of mounted state
        // This fixes the infinite loading issue
        console.log('✅ Setting loading to false (always)');
        setLoading(false);
      }
    };
    
    loadSessionData();
    
    return () => {
      console.log('🧹 useEffect cleanup triggered');
      mounted = false;
      
      // Only abort if the request is still pending
      if (!abortController.signal.aborted) {
        console.log('🚫 Aborting ongoing request due to cleanup');
        abortController.abort();
      } else {
        console.log('ℹ️ Request already completed, no need to abort');
      }
      
      console.log('🧹 Component unmounting, cleaning up...');
      
      // Stop and cleanup RecordRTC recorders
      if (audioRecorderRef.current) {
        try {
          if (audioRecorderRef.current.getState() === 'recording') {
            audioRecorderRef.current.stopRecording();
          }
          audioRecorderRef.current.destroy();
        } catch (e) {
          console.warn('Error stopping audio recorder on unmount:', e);
        }
      }
      
      if (videoRecorderRef.current) {
        try {
          if (videoRecorderRef.current.getState() === 'recording') {
            videoRecorderRef.current.stopRecording();
          }
          videoRecorderRef.current.destroy();
        } catch (e) {
          console.warn('Error stopping video recorder on unmount:', e);
        }
      }
      
      // Stop existing media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [token, navigate]);

  // Initialize camera preview when step changes to questionnaire or question changes
  useEffect(() => {
    if (step === 'questionnaire' && questions.length > 0) {
      console.log('🎬 useEffect triggered - step:', step, 'questionIndex:', currentQuestionIndex);
      
      // Reset camera state for new question/step
      resetCameraState();
      
      // Small delay to ensure cleanup is complete
      const timeoutId = setTimeout(() => {
        initializeCameraPreview();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [step, currentQuestionIndex, questions.length]);

  const refreshQuestions = async () => {
    try {
      const response = await recordingAPI.getSessionQuestions(token);
      setQuestions(response.data.questions);
      setCompletionStatus(response.data.completion_status);
    } catch (error) {
      console.error('Error refreshing questions:', error);
    }
  };

  const getCurrentQuestion = () => {
    return questions[currentQuestionIndex];
  };

  const cleanupRecordRTC = () => {
    console.log('🧹 Cleaning up RecordRTC recorders...');
    
    // Stop and cleanup RecordRTC recorders
    if (audioRecorderRef.current) {
      try {
        if (audioRecorderRef.current.getState() === 'recording') {
          console.log('Stopping existing audio recorder');
          audioRecorderRef.current.stopRecording();
        }
        audioRecorderRef.current.destroy();
      } catch (e) {
        console.warn('Error stopping audio recorder:', e);
      }
      audioRecorderRef.current = null;
    }
    
    if (videoRecorderRef.current) {
      try {
        if (videoRecorderRef.current.getState() === 'recording') {
          console.log('Stopping existing video recorder');
          videoRecorderRef.current.stopRecording();
        }
        videoRecorderRef.current.destroy();
      } catch (e) {
        console.warn('Error stopping video recorder:', e);
      }
      videoRecorderRef.current = null;
    }
    
    // Stop existing media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track:`, track.label);
      });
      streamRef.current = null;
    }
    
    // Clear video preview
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    console.log('✅ RecordRTC cleanup completed');
  };

  const startQuestionnaire = () => {
    cleanupRecordRTC(); // Clean up before starting
    setStep('questionnaire');
    setCurrentQuestionIndex(0);
  };

  const checkMediaPermissions = async () => {
    try {
      // Check if permissions API is available
      if (navigator.permissions) {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
        
        console.log('Permissions status:', {
          camera: cameraPermission.state,
          microphone: microphonePermission.state
        });
        
        return {
          camera: cameraPermission.state,
          microphone: microphonePermission.state
        };
      }
    } catch (error) {
      console.log('Permissions API not available:', error);
    }
    return null;
  };

  // Reset camera state and cleanup
  const resetCameraState = () => {
    console.log('🔄 Resetting camera state...');
    setCameraInitialized(false);
    setCameraError(null);
    initializingRef.current = false;
    
    // Stop existing camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track:`, track.label);
      });
      streamRef.current = null;
    }
    
    // Clear video preview
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Initialize camera preview (separate from recording)
  const initializeCameraPreview = async () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    // If camera is already initialized for this question type, don't reinitialize
    if (cameraInitialized) return;
    
    // Prevent multiple simultaneous initialization attempts
    if (initializingRef.current) {
      console.log('🔄 Camera initialization already in progress, skipping...');
      return;
    }

    try {
      initializingRef.current = true;
      setCameraError(null);
      console.log('🎥 Initializing camera preview for question type:', currentQuestion.question_type);
      
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }

      // Determine constraints based on question type
      const constraints = {};
      if (currentQuestion.question_type === 'audio_video') {
        constraints.video = {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        };
        constraints.audio = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        };
      } else if (currentQuestion.question_type === 'video_only') {
        constraints.video = {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        };
      } else if (currentQuestion.question_type === 'audio_only') {
        // For audio-only, we don't need camera preview
        setCameraInitialized(true);
        return;
      }

      console.log('🎥 Requesting media with constraints:', constraints);

      // Add timeout to getUserMedia request
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Camera permission timeout after 15 seconds. Please check your browser permissions.')), 15000)
      );

      const mediaPromise = navigator.mediaDevices.getUserMedia(constraints);

      // Race between media request and timeout
      const stream = await Promise.race([mediaPromise, timeoutPromise]);
      
      console.log('✅ Camera preview stream obtained:', {
        audioTracks: stream.getAudioTracks().length,
        videoTracks: stream.getVideoTracks().length
      });

      // Store stream reference
      streamRef.current = stream;

      // Display video preview if video is included
      if (constraints.video && videoRef.current) {
        console.log('🎥 Setting video source and starting preview...');
        
        try {
          // Set the stream as source
          videoRef.current.srcObject = stream;
          console.log('✅ Video source set successfully');
          
          // Wait for the video to be ready
          await new Promise((resolve, reject) => {
            const video = videoRef.current;
            
            const onLoadedMetadata = () => {
              console.log('✅ Video metadata loaded');
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (error) => {
              console.error('❌ Video element error:', error);
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Video element failed to load'));
            };
            
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Video metadata load timeout'));
            }, 5000);
          });
          
          // Now try to play the video
          console.log('🎥 Attempting to play video...');
          await videoRef.current.play();
          console.log('✅ Camera preview started successfully');
          setCameraInitialized(true);
          
        } catch (videoError) {
          console.error('❌ Video play failed:', videoError);
          
          // Even if video play fails, we can still consider camera initialized
          // because we have the stream - the user might need to click to play
          console.log('⚠️ Video autoplay failed, but camera stream is available');
          setCameraInitialized(true);
        }
      } else {
        console.log('✅ Audio-only question, no video preview needed');
        setCameraInitialized(true);
      }
      
    } catch (error) {
      console.error('❌ Camera preview initialization failed:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please connect a camera and try again.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is being used by another application. Please close other apps and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Camera permission request timed out. Please refresh and try again.';
      }
      
      setCameraError(errorMessage);
      toast.error(`Camera access failed: ${errorMessage}`);
    } finally {
      initializingRef.current = false;
    }
  };

  const startRecording = async () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    try {
      console.log('🎬 Starting RecordRTC recording for question type:', currentQuestion.question_type);
      
      let stream = streamRef.current;
      
      // If we don't have a stream yet (e.g., for audio_only questions), get one now
      if (!stream) {
        console.log('🎥 No existing stream, requesting media access...');
        
        const constraints = {};
        if (currentQuestion.question_type === 'audio_video') {
          constraints.video = {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          };
          constraints.audio = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          };
        } else if (currentQuestion.question_type === 'audio_only') {
          constraints.audio = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          };
        } else if (currentQuestion.question_type === 'video_only') {
          constraints.video = {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          };
        }

        stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        
        // If video and we have a video element, start preview
        if (constraints.video && videoRef.current && !cameraInitialized) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setCameraInitialized(true);
        }
      } else {
        console.log('✅ Using existing camera stream for recording');
      }
      
      // Create RecordRTC instances based on question type
      if (currentQuestion.question_type === 'audio_video') {
        // For audio+video, create a single recorder that captures both
        console.log('📹 Creating combined audio+video recorder...');
        
        const recorder = new RecordRTC(stream, {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8,opus',
          audioBitsPerSecond: 128000,
          videoBitsPerSecond: 1000000,
          canvas: {
            width: 640,
            height: 480
          },
          frameInterval: 90
        });

        videoRecorderRef.current = recorder;
        
        // Also create separate audio recorder for better compatibility
        const audioRecorder = new RecordRTC(stream, {
          type: 'audio',
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000,
          numberOfAudioChannels: 1
        });

        audioRecorderRef.current = audioRecorder;
        
      } else if (currentQuestion.question_type === 'audio_only') {
        // Audio-only recording
        console.log('🎵 Creating audio-only recorder...');
        
        const audioRecorder = new RecordRTC(stream, {
          type: 'audio',
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000,
          numberOfAudioChannels: 1,
          timeSlice: 1000,
          ondataavailable: function(blob) {
            console.log('Audio data available:', blob.size, 'bytes');
          }
        });

        audioRecorderRef.current = audioRecorder;
        
      } else if (currentQuestion.question_type === 'video_only') {
        // Video-only recording
        console.log('📹 Creating video-only recorder...');
        
        const videoRecorder = new RecordRTC(stream, {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8',
          videoBitsPerSecond: 1000000,
          canvas: {
            width: 640,
            height: 480
          },
          frameInterval: 90
        });

        videoRecorderRef.current = videoRecorder;
      }

      // Start recording
      console.log('🔴 Starting RecordRTC recording...');
      
      if (audioRecorderRef.current) {
        audioRecorderRef.current.startRecording();
        console.log('✅ Audio recording started');
      }
      
      if (videoRecorderRef.current) {
        videoRecorderRef.current.startRecording();
        console.log('✅ Video recording started');
      }

      setRecording(true);
      setStep('recording');

      // Start timer
      setRecordingTime(0);
      const duration = currentQuestion.recording_duration || 30;
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= duration) {
            stopRecording();
            return duration;
          }
          return prev + 1;
        });
      }, 1000);

      console.log('🎊 Recording started successfully!');

    } catch (error) {
      console.error('Error starting recording:', error);
      console.log('Error details:', {
        name: error.name,
        message: error.message,
        constraint: error.constraint
      });
      
      if (error.name === 'NotAllowedError') {
        toast.error('Camera and microphone permissions are required. Please click "Allow" when prompted and try again.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera or microphone found. Please connect a camera/microphone and refresh the page.');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Camera/microphone constraints not supported. Trying with basic settings...');
        // Could trigger a retry with simpler constraints here
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera/microphone is already in use by another application. Please close other apps and try again.');
      } else if (error.name === 'SecurityError') {
        toast.error('Camera/microphone access blocked. Please ensure you are using HTTPS and allow permissions.');
      } else {
        toast.error(`Unable to access camera and microphone: ${error.message || 'Unknown error'}. Please check your browser permissions and device settings.`);
      }
    }
  };

  const stopRecording = () => {
    if (recording) {
      console.log('🛑 Stopping RecordRTC recording...');
      
      // Set uploading state immediately for button feedback
      setUploading(true);
      
      // Stop RecordRTC recorders
      if (audioRecorderRef.current) {
        audioRecorderRef.current.stopRecording(() => {
          console.log('✅ Audio recording stopped');
          const audioBlob = audioRecorderRef.current.getBlob();
          console.log('Audio blob size:', audioBlob.size, 'bytes');
          setAudioBlob(audioBlob);
        });
      }
      
      if (videoRecorderRef.current) {
        videoRecorderRef.current.stopRecording(() => {
          console.log('✅ Video recording stopped');
          const videoBlob = videoRecorderRef.current.getBlob();
          console.log('Video blob size:', videoBlob.size, 'bytes');
          setVideoBlob(videoBlob);
          
          // Trigger upload after both recordings are done
          setTimeout(() => {
            setStep('uploading');
            uploadCurrentQuestionRecording();
          }, 1000);
        });
      } else if (audioRecorderRef.current) {
        // Audio-only case - trigger upload after audio is done
        setTimeout(() => {
          setStep('uploading');
          uploadCurrentQuestionRecording();
        }, 1000);
      }
      
      setRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const uploadCurrentQuestionRecording = async () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    setUploading(true);
    try {
      // Convert blobs to files
      const audioFile = audioBlob ? new File([audioBlob], 'audio.webm', { type: 'audio/webm' }) : null;
      const videoFile = videoBlob ? new File([videoBlob], 'video.webm', { type: 'video/webm' }) : null;

      const response = await recordingAPI.uploadQuestionRecording(
        token,
        currentQuestion.response_id,
        currentQuestion.question_id,
        audioFile,
        videoFile
      );

      // Update completion status
      setCompletionStatus(response.data.completion_status);
      
      // Refresh questions to get updated status
      await refreshQuestions();
      
      toast.success(`Question ${currentQuestionIndex + 1} recorded successfully!`);
      
      // Move to next question or complete session
      if (currentQuestionIndex + 1 < questions.length) {
        nextQuestion();
      } else {
        completeSession();
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload recording');
      setStep('questionnaire'); // Go back to questionnaire
    } finally {
      setUploading(false);
      // Reset recording data
      setAudioBlob(null);
      setVideoBlob(null);
    }
  };

  const nextQuestion = async () => {
    if (currentQuestionIndex + 1 < questions.length && !navigating) {
      setNavigating(true);
      setNavigationDirection('next');
      
      // Clean up current recording session before moving to next question
      cleanupRecordRTC();
      
      // Simulate smooth transition
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setStep('questionnaire');
      
      setNavigating(false);
      setNavigationDirection(null);
    }
  };

  const previousQuestion = async () => {
    if (currentQuestionIndex > 0 && !navigating) {
      setNavigating(true);
      setNavigationDirection('previous');
      
      // Clean up current recording session before moving to previous question
      cleanupRecordRTC();
      
      // Simulate smooth transition
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setStep('questionnaire');
      
      setNavigating(false);
      setNavigationDirection(null);
    }
  };

  const skipQuestion = async () => {
    toast('Question skipped', {
      icon: 'ℹ️',
      duration: 2000
    });
    if (currentQuestionIndex + 1 < questions.length) {
      nextQuestion();
    } else {
      completeSession();
    }
  };

  const completeSession = async () => {
    try {
      setStep('uploading');
      const response = await recordingAPI.completeSession(token, sessionData.session_id);
      setFinalVitals(response.data.vitals);
      setStep('results');
      toast.success('All recordings completed successfully!');
    } catch (error) {
      console.error('Complete session error:', error);
      toast.error('Failed to complete session');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'audio_video': return 'Audio & Video';
      case 'audio_only': return 'Audio Only';
      case 'video_only': return 'Video Only';
      default: return 'Recording';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recording session...</p>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Not Found</h2>
          <p className="text-gray-600 mb-4">This recording link is invalid or has expired.</p>
          <p className="text-sm text-gray-500">You will be redirected shortly...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = getCurrentQuestion();
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-va-primary-50 to-va-accent-100">
      <div className="container mx-auto px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center mb-4">
            <Heart className="h-8 w-8 sm:h-12 sm:w-12 text-red-500 pulse-heart" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-va-neutral-900 mb-2">VitalAudio Recording</h1>
          <p className="text-va-neutral-600 text-sm sm:text-base">Hello {sessionData.patient_name}</p>
          
          {/* Progress Bar */}
          {step !== 'instructions' && questions.length > 0 && (
            <div className="mt-4 sm:mt-6 max-w-md mx-auto">
              <div className="flex justify-between text-xs sm:text-sm text-va-neutral-600 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="w-full bg-va-neutral-200 rounded-full h-2">
                <div 
                  className="bg-brand-blue h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto">
          {step === 'instructions' && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                Recording Instructions
              </h2>
              
              <div className="space-y-4 mb-8">
                {sessionData.instructions.map((instruction, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm font-medium">{index + 1}</span>
                    </div>
                    <p className="text-gray-700">{instruction}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-medium text-blue-900 mb-2">What to Expect:</h3>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>You'll answer {questions.length} questions</li>
                  <li>Each question requires a short recording</li>
                  <li>Recording duration varies per question (15-45 seconds)</li>
                  <li>Follow the specific instructions for each question</li>
                  <li>Your data is processed securely</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-medium text-amber-900 mb-2 flex items-center">
                  <Camera className="mr-2 h-5 w-5" />
                  Camera & Microphone Permissions Required
                </h3>
                <div className="space-y-2 text-amber-800">
                  <p>This questionnaire requires access to your camera and microphone to record your responses.</p>
                  <div className="text-sm">
                    <p className="font-medium">When prompted by your browser:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Click <strong>"Allow"</strong> for camera access</li>
                      <li>Click <strong>"Allow"</strong> for microphone access</li>
                      <li>If blocked, click the camera icon in your address bar to enable permissions</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-8">
                <details className="text-sm">
                  <summary className="font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                    Troubleshooting: Camera/Microphone Issues
                  </summary>
                  <div className="mt-3 space-y-2 text-gray-600">
                    <p><strong>If permissions are blocked:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Look for a camera icon in your browser's address bar and click it</li>
                      <li>Select "Allow" for both camera and microphone</li>
                      <li>Refresh the page and try again</li>
                    </ul>
                    <p className="pt-2"><strong>Still having issues?</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Make sure no other apps are using your camera/microphone</li>
                      <li>Try a different browser (Chrome, Firefox, Safari, Edge)</li>
                      <li>Check your device camera/microphone settings</li>
                      <li>Ensure you're using a secure (HTTPS) connection</li>
                    </ul>
                  </div>
                </details>
              </div>

              <div className="text-center">
                <button
                  onClick={startQuestionnaire}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-colors flex items-center mx-auto"
                >
                  <FileText className="mr-3 h-6 w-6" />
                  Start Questionnaire
                </button>
              </div>
            </div>
          )}

          {step === 'questionnaire' && currentQuestion && (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </h2>
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  {getQuestionTypeLabel(currentQuestion.question_type)}
                </div>
              </div>

              <div className="mb-8">
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {currentQuestion.question_text}
                  </h3>
                  {currentQuestion.instruction_text && (
                    <p className="text-gray-600 text-sm">
                      <strong>Instructions:</strong> {currentQuestion.instruction_text}
                    </p>
                  )}
                </div>

                <div className="bg-amber-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-amber-900 mb-2">Important Reminders:</h4>
                  <ul className="list-disc list-inside space-y-1 text-amber-800 text-sm">
                    <li>Keep your phone near you with no disturbances</li>
                    <li>Ensure clear microphone access</li>
                    <li>Make sure you have good lighting</li>
                    <li>Recording duration: {currentQuestion.recording_duration} seconds</li>
                  </ul>
                </div>
              </div>

              {/* Camera Preview Section */}
              {currentQuestion.question_type !== 'audio_only' && (
                <div className="mb-8">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 text-center">Camera Preview</h4>
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden mx-auto max-w-2xl" style={{ aspectRatio: '4/3' }}>
                    {cameraError ? (
                      <div className="flex items-center justify-center h-full text-white">
                        <div className="text-center">
                          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                          <p className="text-lg font-medium mb-2">Camera Access Failed</p>
                          <p className="text-sm text-gray-300 mb-4">{cameraError}</p>
                          <button
                            onClick={initializeCameraPreview}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    ) : !cameraInitialized ? (
                      <div className="flex items-center justify-center h-full text-white">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                          <p className="text-lg font-medium">Initializing Camera...</p>
                          <p className="text-sm text-gray-300 mt-2">Please allow camera access when prompted</p>
                          <div className="mt-4 space-x-2">
                            <button
                              onClick={() => {
                                setCameraError('Camera initialization cancelled by user');
                                console.log('Camera initialization cancelled by user');
                              }}
                              className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                resetCameraState();
                                initializeCameraPreview();
                              }}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                          style={{ transform: 'scaleX(-1)' }} // Mirror effect for better user experience
                        />
                        <div className="absolute top-4 left-4 flex items-center space-x-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-white text-sm font-medium">Camera Ready</span>
                        </div>
                      </>
                    )}
                  </div>
                  {cameraInitialized && !cameraError && (
                    <p className="text-center text-sm text-gray-600 mt-2">
                      Your camera is ready. Click "Start Recording" when you're prepared to answer the question.
                    </p>
                  )}
                </div>
              )}

              {/* Audio-only message */}
              {currentQuestion.question_type === 'audio_only' && (
                <div className="mb-8">
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <Mic className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-blue-900 mb-2">Audio-Only Recording</h4>
                    <p className="text-blue-800">This question only requires audio recording. No camera preview is needed.</p>
                  </div>
                </div>
              )}

              {/* Navigation and Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-2xl mx-auto">
                {/* Previous Button */}
                <button
                  onClick={previousQuestion}
                  disabled={currentQuestionIndex === 0 || navigating}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[120px]"
                >
                  {navigating && navigationDirection === 'previous' ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      Previous
                    </>
                  )}
                </button>

                {/* Start Recording Button */}
                <button
                  onClick={startRecording}
                  disabled={navigating}
                  className="w-full sm:w-auto bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-colors flex items-center justify-center min-w-[160px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="mr-3 h-6 w-6" />
                  Start Recording
                </button>

                {/* Next/Skip Buttons */}
                <div className="flex gap-2 w-full sm:w-auto">
                  {currentQuestionIndex + 1 < questions.length && (
                    <button
                      onClick={nextQuestion}
                      disabled={navigating}
                      className="flex-1 sm:flex-none flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[100px]"
                    >
                      {navigating && navigationDirection === 'next' ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          Next
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  )}
                  
                  <button
                    onClick={skipQuestion}
                    disabled={navigating}
                    className="flex-1 sm:flex-none flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[100px]"
                  >
                    <SkipForward className="mr-2 h-5 w-5" />
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'recording' && currentQuestion && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  Recording Question {currentQuestionIndex + 1}
                </h2>
                <div className="flex items-center justify-center space-x-4 text-red-600">
                  <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                  <span className="text-xl font-mono">
                    {formatTime(recordingTime)} / {formatTime(currentQuestion.recording_duration)}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-center text-lg font-medium text-gray-800 mb-4">
                  {currentQuestion.question_text}
                </p>
              </div>

              {/* Video Display */}
              {currentQuestion.question_type !== 'audio_only' && (
                <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-6 mx-auto max-w-4xl" style={{ aspectRatio: '16/9' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }} // Mirror effect for better user experience
                  />
                  
                  {/* Recording Indicator */}
                  <div className="absolute top-4 left-4 flex items-center space-x-2">
                    <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                      <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                      REC
                    </div>
                    <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {formatTime(recordingTime)}
                    </div>
                  </div>

                  {/* Device Indicators */}
                  <div className="absolute top-4 right-4 flex space-x-2">
                    {currentQuestion.question_type !== 'audio_only' && (
                      <div className="bg-black bg-opacity-50 text-white p-2 rounded-full">
                        <Camera className="h-5 w-5" />
                      </div>
                    )}
                    {currentQuestion.question_type !== 'video_only' && (
                      <div className="bg-black bg-opacity-50 text-white p-2 rounded-full">
                        <Mic className="h-5 w-5" />
                      </div>
                    )}
                  </div>

                  {/* Recording Status Overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-center">
                      <p className="text-sm">
                        {currentQuestion.question_text}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Audio-Only Display */}
              {currentQuestion.question_type === 'audio_only' && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-8 mb-6 text-center mx-auto max-w-2xl">
                  <div className="relative">
                    <Mic className="h-24 w-24 text-blue-500 mx-auto mb-4" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-32 h-32 border-4 border-blue-300 rounded-full animate-ping opacity-30"></div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Audio Recording in Progress</h3>
                  <p className="text-gray-600 mb-4">{currentQuestion.question_text}</p>
                  <div className="bg-white rounded-full px-4 py-2 inline-block">
                    <span className="text-blue-600 font-mono text-lg">
                      {formatTime(recordingTime)} / {formatTime(currentQuestion.recording_duration)}
                    </span>
                  </div>
                </div>
              )}

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div 
                  className="bg-red-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(recordingTime / currentQuestion.recording_duration) * 100}%` }}
                ></div>
              </div>

              <div className="text-center">
                <button
                  onClick={stopRecording}
                  disabled={uploading}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors flex items-center mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending Recording...
                    </>
                  ) : (
                    <>
                      <Square className="mr-2 h-5 w-5" />
                      Stop Recording
                    </>
                  )}
                </button>
                
                {uploading && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                      Processing your recording, please wait...
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'uploading' && (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="mb-6">
                <Upload className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {currentQuestionIndex + 1 >= questions.length ? 'Processing Your Recordings' : 'Saving Your Recording'}
                </h2>
                <p className="text-gray-600">
                  {currentQuestionIndex + 1 >= questions.length 
                    ? 'Please wait while we analyze all your recordings...' 
                    : 'Please wait while we save your recording...'}
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
                </div>
                <p className="text-sm text-gray-500">
                  {completionStatus && (
                    `${completionStatus.completed_questions + 1}/${completionStatus.total_questions} questions completed`
                  )}
                </p>
              </div>
            </div>
          )}

          {step === 'results' && finalVitals && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Recording Complete!</h2>
                <p className="text-gray-600">
                  Your vital signs have been successfully processed from your {questions.length} recordings 
                  and sent to your healthcare provider.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="text-center p-6 bg-red-50 rounded-lg">
                  <Heart className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{finalVitals.heart_rate}</p>
                  <p className="text-gray-600">Heart Rate (BPM)</p>
                </div>

                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <Timer className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{finalVitals.heart_rate_variability}</p>
                  <p className="text-gray-600">HRV (ms)</p>
                </div>

                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <div className="h-8 w-8 bg-purple-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">R</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{finalVitals.rhythm}</p>
                  <p className="text-gray-600">Rhythm</p>
                </div>

                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="h-8 w-8 bg-green-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">BP</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{finalVitals.blood_pressure}</p>
                  <p className="text-gray-600">Blood Pressure</p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Thank you for completing the questionnaire! Results have been automatically 
                  shared with your healthcare provider.
                </p>
                <button
                  onClick={() => window.close()}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors"
                >
                  Close Window
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientRecording;